import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { type KeyboardEvent, type RefObject, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SendIcon } from '../icons/SendIcon';
import { inputSx, rootSx, sendButtonSx } from './styles';

export type InputRefType = RefObject<{ setValue: (value: string) => void } | null>;

export type ChatInputProps = {
  sendButton: {
    label?: string;
    onClick: (message: string) => void;
    isLoading?: boolean;
  };
  minRows?: number;
  maxRows?: number;
  placeholder?: string;
  inputRef?: InputRefType;
};

export const ChatInput = ({ sendButton, minRows = 1, maxRows = 5, placeholder, inputRef }: ChatInputProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const inputRefInternal = useRef<HTMLInputElement>(null);
  const { isLoading: isSendButtonLoading, onClick: sendSendButtonClick, label: sendSendButtonLabel } = sendButton;

  useImperativeHandle(inputRef, () => ({
    setValue: (value: string) => {
      setQuery(value);
      if (inputRefInternal.current) {
        inputRefInternal.current.focus();
      }
    },
  }));

  const handleSendMessage = useCallback(() => {
    sendSendButtonClick(query.trim());
    setQuery('');
  }, [sendSendButtonClick, query]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && !event.shiftKey && !isSendButtonLoading) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, isSendButtonLoading]
  );

  return (
    <Box sx={rootSx}>
      <TextField
        autoFocus={true}
        multiline
        minRows={minRows}
        maxRows={maxRows}
        fullWidth
        variant="standard"
        placeholder={placeholder ?? t('Where would you like to start?')}
        aria-label={'Chat input'}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        inputRef={inputRefInternal}
        slotProps={{
          input: {
            disableUnderline: true,
            autoFocus: true,
            sx: inputSx,
          },
        }}
      />

      <IconButton
        aria-label={sendSendButtonLabel ?? t('Ask')}
        onClick={handleSendMessage}
        disabled={!query.trim() || !!isSendButtonLoading}
        sx={sendButtonSx}
      >
        {isSendButtonLoading ? <CircularProgress color="inherit" size={15} /> : <SendIcon />}
      </IconButton>
    </Box>
  );
};
